import { useState } from 'preact/hooks';

/**
 * Tool tile for the launcher grid.
 *
 * Variants:
 * - Internal tool (default): 9px coloured dot in the module's locked ink.
 * - External site (`external` prop): site favicon via Google's S2 service,
 *   opens in a new tab. Falls back to a neutral dot if the favicon errors.
 */
const SiteIcon = ({ href }) => {
  const [errored, setErrored] = useState(false);
  let domain;
  try {
    domain = new URL(href).hostname;
  } catch {
    domain = null;
  }
  if (errored || !domain) {
    return (
      <span
        class="block rounded-full"
        style={{ background: '#A8A095', width: 9, height: 9 }}
        aria-hidden="true"
      />
    );
  }
  return (
    <img
      src={`https://www.google.com/s2/favicons?sz=64&domain=${domain}`}
      alt=""
      width={18}
      height={18}
      class="block rounded-full bg-white object-cover"
      onError={() => setErrored(true)}
      aria-hidden="true"
    />
  );
};

const Dot = ({ color }) => (
  <span
    class="block rounded-full"
    style={{ background: color, width: 9, height: 9 }}
    aria-hidden="true"
  />
);

export const ToolTile = ({ href, ink, title, subtitle, external = false }) => {
  const indicator = external ? <SiteIcon href={href} /> : <Dot color={ink ?? '#FAF6EC'} />;
  const indentClass = external ? 'ml-[26px]' : 'ml-[18px]';

  const content = (
    <>
      <div class="mb-1.5 flex items-center gap-2">
        {indicator}
        <p class="m-0 font-serif text-[17px] font-medium text-text-primary">
          {title}
        </p>
      </div>
      <p class={`m-0 text-[13px] font-medium text-text-tertiary ${indentClass}`}>
        {subtitle}
      </p>
    </>
  );

  const classes =
    'block rounded-lg border border-border-subtle bg-bg-card p-[18px] transition hover:border-border-strong hover:bg-bg-hover focus:outline-none focus-visible:ring-2 focus-visible:ring-brand';

  if (!href) return <div class={classes}>{content}</div>;

  return external ? (
    <a class={classes} href={href} target="_blank" rel="noopener noreferrer">
      {content}
    </a>
  ) : (
    <a class={classes} href={href}>
      {content}
    </a>
  );
};
